// NapflixOS Universal Search Engine v1
// Safe read-only search provider for existing Napflix app/API data.
const SEARCH_LIMIT = 20;

let homeCache = null;
let pjtvCache = null;
let musicArtistsCache = null;
let musicAlbumsCache = null;
let musicVideosCache = null;

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getArtwork = (item) =>
  item?.thumb || item?.art || item?.poster || item?.posterUrl || item?.logo || null;

const resultId = (prefix, item, fallback) =>
  `${prefix}:${item?.ratingKey || item?.id || item?.key || item?.number || fallback}`;

const safeFetchJson = async (url) => {
  const response = await fetch(url, { cache: "force-cache" });
  if (!response.ok) throw new Error(`${url} ${response.status}`);
  return response.json();
};

const loadHome = async () => {
  if (!homeCache) {
    homeCache = safeFetchJson("/api/cache/home").catch((error) => {
      homeCache = null;
      throw error;
    });
  }

  return homeCache;
};

const loadPJTV = async () => {
  if (!pjtvCache) {
    pjtvCache = safeFetchJson("/pjtv/channels").catch((error) => {
      pjtvCache = null;
      throw error;
    });
  }

  return pjtvCache;
};

const plexSearchUrl = (path) =>
  `/api/plex?path=${encodeURIComponent(path)}`;

const loadMusicArtists = async () => {
  if (!musicArtistsCache) {
    musicArtistsCache = safeFetchJson(
      plexSearchUrl(
        "/library/sections/40/all?type=8&sort=titleSort:asc&X-Plex-Container-Start=0&X-Plex-Container-Size=2000"
      )
    ).catch((error) => {
      musicArtistsCache = null;
      throw error;
    });
  }

  return musicArtistsCache;
};

const loadMusicAlbums = async () => {
  if (!musicAlbumsCache) {
    musicAlbumsCache = safeFetchJson(
      plexSearchUrl(
        "/library/sections/40/all?type=9&sort=addedAt:desc&X-Plex-Container-Start=0&X-Plex-Container-Size=8000"
      )
    ).catch((error) => {
      musicAlbumsCache = null;
      throw error;
    });
  }

  return musicAlbumsCache;
};

const loadMusicVideos = async () => {
  if (!musicVideosCache) {
    musicVideosCache = safeFetchJson(
      plexSearchUrl("/library/sections/33/all")
    ).catch((error) => {
      musicVideosCache = null;
      throw error;
    });
  }

  return musicVideosCache;
};

const scoreResult = (query, haystack) => {
  const normalizedHaystack = normalize(haystack);
  if (!query || !normalizedHaystack) return 0;
  if (normalizedHaystack === query) return 100;
  if (normalizedHaystack.startsWith(query)) return 80;
  if (normalizedHaystack.includes(query)) return 60;

  const tokens = query.split(" ").filter(Boolean);
  const matched = tokens.filter((token) => normalizedHaystack.includes(token));
  return matched.length ? 25 + matched.length * 5 : 0;
};

const buildMediaResults = (items, type, sourceLabel) =>
  (items || []).map((item, index) => ({
    id: resultId(type, item, index),
    type,
    title: item?.title || item?.name || "Untitled",
    subtitle: [item?.year, item?.rating, item?.contentRating].filter(Boolean).join(" - "),
    artwork: getArtwork(item),
    source: sourceLabel,
    payload: item,
  }));

const buildPJTVResults = (channels) =>
  (channels || []).map((channel, index) => ({
    id: resultId("pjtv-channel", channel, index),
    type: "pjtv-channel",
    title: channel?.displayName || channel?.guideLabel || channel?.name || "PJTV Channel",
    subtitle: [channel?.number, channel?.category || channel?.type || "PJTV"]
      .filter(Boolean)
      .join(" - "),
    artwork: channel?.logo || null,
    source: "PJTV",
    payload: {
      channelNumber: channel?.number,
      name: channel?.name,
      guideLabel: channel?.guideLabel,
      channel,
    },
  }));

const buildMusicArtistResults = (artists) =>
  (artists || [])
    .filter((artist) => String(artist?.librarySectionID) !== "37")
    .map((artist, index) => ({
      id: resultId("music-artist", artist, index),
      type: "music-artist",
      title: artist?.title || "Unknown Artist",
      subtitle: "Artist",
      artwork: getArtwork(artist),
      source: "Music",
      payload: {
        ratingKey: artist?.ratingKey,
        item: artist,
      },
    }));

const buildMusicAlbumResults = (albums) =>
  (albums || []).map((album, index) => ({
    id: resultId("music-album", album, index),
    type: "music-album",
    title: album?.title || "Untitled Album",
    subtitle: [album?.parentTitle, album?.year || album?.parentYear, "Album"]
      .filter(Boolean)
      .join(" - "),
    artwork: getArtwork(album),
    source: "Music",
    payload: {
      ratingKey: album?.ratingKey,
      artist: album?.parentTitle,
      item: album,
    },
  }));

const buildMusicVideoResults = (videos) =>
  (videos || []).map((video, index) => ({
    id: resultId("music-video", video, index),
    type: "music-video",
    title: video?.title || "Untitled Music Video",
    subtitle: [video?.parentTitle, "Music Video"].filter(Boolean).join(" - "),
    artwork: getArtwork(video),
    source: "Music",
    payload: {
      ratingKey: video?.ratingKey,
      artist: video?.parentTitle,
      item: video,
    },
  }));

export async function searchNapflix(query) {
  const normalizedQuery = normalize(query);
  if (normalizedQuery.length < 2) {
    return [];
  }

  const [homeResult, pjtvResult, artistsResult, albumsResult, videosResult] = await Promise.allSettled([
    loadHome(),
    loadPJTV(),
    loadMusicArtists(),
    loadMusicAlbums(),
    loadMusicVideos(),
  ]);

  const home = homeResult.status === "fulfilled" ? homeResult.value : {};
  const pjtvData = pjtvResult.status === "fulfilled" ? pjtvResult.value : [];
  const artistsData = artistsResult.status === "fulfilled" ? artistsResult.value : {};
  const albumsData = albumsResult.status === "fulfilled" ? albumsResult.value : {};
  const videosData = videosResult.status === "fulfilled" ? videosResult.value : {};
  const pjtvChannels = Array.isArray(pjtvData)
    ? pjtvData
    : Object.values(pjtvData || {});
  const musicArtists = artistsData?.MediaContainer?.Metadata || artistsData?.Metadata || [];
  const musicAlbums = albumsData?.MediaContainer?.Metadata || albumsData?.Metadata || [];
  const musicVideos = videosData?.MediaContainer?.Metadata || videosData?.Metadata || [];

  const candidates = [
    ...buildMediaResults(home?.movies, "movie", "Movies"),
    ...buildMediaResults(home?.shows, "show", "TV Shows"),
    ...buildPJTVResults(pjtvChannels),
    ...buildMusicArtistResults(musicArtists),
    ...buildMusicAlbumResults(musicAlbums),
    ...buildMusicVideoResults(musicVideos),
  ];

  return candidates
    .map((result) => ({
      ...result,
      searchScore: scoreResult(
        normalizedQuery,
        `${result.title} ${result.subtitle} ${result.source}`
      ),
    }))
    .filter((result) => result.searchScore > 0)
    .sort((a, b) => b.searchScore - a.searchScore || a.title.localeCompare(b.title))
    .slice(0, SEARCH_LIMIT)
    .map(({ searchScore, ...result }) => result);
}
